import { useState } from "react";
import { useEventsFeatureFlags } from "@circle-react/hooks/useEventsFeatureFlags";
import type { Event, FormScheduleValues } from "@circle-react/types";
import type { ButtonProps } from "@circle-react-shared/uikit/Button";
import { Button } from "@circle-react-uikit/Button";
import { RECURRING_EDIT_MODE } from "../../utils";
import { isDraft } from "../../utils/helpers";
import { EditRecurringModal } from "../EditRecurringModal";
import { ScheduleUpdateConfirmationModal } from "../EditRecurringModal/ScheduleUpdateConfirmationModal";

type RecurringEditMode =
  (typeof RECURRING_EDIT_MODE)[keyof typeof RECURRING_EDIT_MODE];

interface SubmitEditButtonProps extends Omit<ButtonProps, "onClick"> {
  children: React.ReactNode;
  event?: Event | null;
  formScheduleValues?: FormScheduleValues;
  isPublishing?: boolean;
  isRecurringEvent?: boolean;
  isRepeatsDirty?: boolean;
  isStartsAtDirty?: boolean;
  onEditModeConfirm: (editMode: RecurringEditMode) => void;
  shouldHideAllEventOption?: boolean;
  type?: "button" | "submit" | "reset";
}

export const SubmitEditButton = ({
  onEditModeConfirm,
  isRecurringEvent = false,
  isPublishing = false,
  children,
  isStartsAtDirty = false,
  isRepeatsDirty = false,
  shouldHideAllEventOption = false,
  type = "button",
  event,
  formScheduleValues = {
    startsAt: "",
    endsAt: "",
    frequency: "",
  } as FormScheduleValues,
  ...passthroughProps
}: SubmitEditButtonProps) => {
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [isScheduleUpdateModalOpen, setIsScheduleUpdateModalOpen] =
    useState(false);
  const [pendingEditMode, setPendingEditMode] =
    useState<RecurringEditMode | null>(null);
  const { hasSplitRecurringEventsEnabled } = useEventsFeatureFlags();

  const onClickHandler = () => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible.
    if (isRecurringEvent) {
      setIsRecurringModalOpen(true);
    } else {
      onEditModeConfirm(RECURRING_EDIT_MODE.CURRENT);
    }
  };

  const onConfirmHandler = (editMode: RecurringEditMode) => {
    setIsRecurringModalOpen(false);

    // Check if we need to show the schedule update confirmation
    if (
      (editMode === RECURRING_EDIT_MODE.REMAINING ||
        editMode === RECURRING_EDIT_MODE.CURRENT) &&
      (isRepeatsDirty || isStartsAtDirty) &&
      event
    ) {
      setPendingEditMode(editMode);
      setIsScheduleUpdateModalOpen(true);
    } else {
      onEditModeConfirm(editMode);
    }
  };

  const onScheduleUpdateConfirm = () => {
    setIsScheduleUpdateModalOpen(false);
    if (pendingEditMode) {
      onEditModeConfirm(pendingEditMode);
      setPendingEditMode(null);
    }
  };

  const onScheduleUpdateCancel = () => {
    setIsScheduleUpdateModalOpen(false);
    setPendingEditMode(null);
  };

  // Determine modal behavior based on feature flag
  const getModalProps = () => {
    if (!hasSplitRecurringEventsEnabled) {
      // Original behavior: show current option only when starts_at is dirty
      return {
        showCurrentOptionOnly: isStartsAtDirty,
        shouldHideAllEventOption,
      };
    }

    // New behavior with feature flag enabled
    if (isRepeatsDirty) {
      // Frequency (Repeats) modified: show only REMAINING option
      return {
        showRemainingOptionOnly: true,
        shouldHideAllEventOption,
        defaultSelection: RECURRING_EDIT_MODE.REMAINING,
      };
    }
    if (isStartsAtDirty) {
      // Only starts_at modified: show CURRENT and REMAINING options
      return {
        showCurrentAndRemainingOnly: true,
        shouldHideAllEventOption,
        defaultSelection: RECURRING_EDIT_MODE.CURRENT,
      };
    }

    // Default: show all options
    return {
      shouldHideAllEventOption,
      defaultSelection: RECURRING_EDIT_MODE.CURRENT,
    };
  };

  return (
    <>
      <Button type={type} onClick={onClickHandler} {...passthroughProps}>
        {children}
      </Button>
      <EditRecurringModal
        show={isRecurringModalOpen}
        onConfirm={onConfirmHandler}
        onCloseModal={() => setIsRecurringModalOpen(false)}
        isPublishing={isPublishing}
        isDraft={!!event && isDraft(event)}
        {...getModalProps()}
      />
      {event && pendingEditMode && (
        <ScheduleUpdateConfirmationModal
          isOpen={isScheduleUpdateModalOpen}
          onConfirm={onScheduleUpdateConfirm}
          onCancel={onScheduleUpdateCancel}
          event={event}
          formScheduleValues={formScheduleValues}
          editMode={pendingEditMode}
        />
      )}
    </>
  );
};
